/**
 * Centralized document processing messages for Docufy AI
 *
 * This file contains all user-facing messages for document upload,
 * processing, extraction, and error handling.
 */

import {
  CONFIDENCE_LOW_FIELD_THRESHOLD,
  CONFIDENCE_OVERALL_DISPLAY_HIGH_MIN,
} from './confidenceReview';

/**
 * Upload & Intake Messages
 */
export const UploadMessages = {
  // Upload - In Progress
  inProgress: {
    uploading: 'Uploading your document…',
    uploadingMultiple: (fileCount: number, progress: number) =>
      `Securely uploading ${fileCount} file${fileCount > 1 ? 's' : ''} (${Math.round(progress)}%). Please don't close this tab.`,
    largeFile: 'Large file detected. Upload may take a few moments.',
  },

  // Upload - Success
  success: {
    preparing: 'Upload complete. Preparing your document for processing.',
    received: 'Document received successfully.',
  },

  // Upload - Backend processing; no real % from the API — rotate copy + elapsed time instead
  backendProcessing: {
    preparingDocument: 'Getting your document ready…',
    /** Cycles every few seconds so the UI never looks frozen */
    rotatingMessages: [
      'Making sense of your document…',
      'Reading through the details…',
      'Preparing everything for you…',
      'Taking a careful look…',
      'Almost there—finishing up…',
      "Hang tight—this won't take long…",
      'Polishing things on our side…',
      'Just a moment longer…',
    ] as const,
    /** Show “Still working — Ns” after this many seconds in processing */
    stillWorkingAfterSeconds: 8,
  },

  // Upload - Validation Errors
  validationErrors: {
    unsupportedFormat: 'Unsupported file format. Please upload PDF, DOCX, PNG, or JPG.',
    fileSizeExceeded: 'File size exceeds the 10MB limit.',
    passwordProtected:
      'This document appears to be password-protected. Please remove the password and retry.',
    corrupted: "We couldn't read this file. It may be corrupted.",
    missingSelections: 'Please select a workspace, AI model, and industry before uploading files.',
    missingModel: 'Please select an AI model to continue with your upload.',
  },
} as const;

/**
 * Pre-Processing Messages (Before AI Runs)
 */
export const PreProcessingMessages = {
  // Document Preparation
  documentPreparation: {
    analyzing: 'Analyzing document structure…',
    optimizing: 'Optimizing pages for accurate extraction.',
    detecting: 'Detecting language and layout.',
  },

  // User Guidance
  userGuidance: {
    multipleSections: 'This document has multiple sections. Results may take slightly longer.',
    lowImageQuality: 'Low image quality detected. Accuracy may be reduced.',
  },
} as const;

/**
 * AI Processing & Extraction Messages
 */
export const ProcessingMessages = {
  // Processing - In Progress
  inProgress: {
    extracting: 'Docufy AI is extracting key information…',
    understanding: 'Understanding tables, forms, and text.',
    processingPage: (current: number, total: number) => `Processing page ${current} of ${total}.`,
    runningModels: 'Running advanced AI models for higher accuracy.',
  },

  // Processing - Long Running
  longRunning: {
    takingLonger: 'This is taking longer than usual due to document complexity.',
    stillWorking: 'Still working—thanks for your patience.',
  },

  // Processing - Partial Completion
  partialCompletion: {
    coreFieldsExtracted: 'Core fields extracted. Additional insights are still processing.',
    someSectionsProcessed: 'Some sections processed successfully. Others are pending review.',
  },
} as const;

/**
 * Success & Completion Messages
 */
export const SuccessMessages = {
  // Full Success
  fullSuccess: {
    complete: 'Processing complete 🎉',
    readyForReview: 'Your document is ready for review.',
    allFieldsExtracted: 'All fields extracted successfully.',
  },

  // Success with Warnings
  successWithWarnings: {
    completeWithIssues: 'Processing complete with minor issues.',
    mostFieldsExtracted: 'Most fields extracted. A few require manual review.',
    handwrittenSections: 'Handwritten sections may need verification.',
  },
} as const;

/**
 * Review & Validation Messages
 */
export const ReviewMessages = {
  // Confidence & Accuracy Indicators
  confidence: {
    high: (percentage: number) => `High confidence extraction (${percentage}%).`,
    medium: `Below ${CONFIDENCE_OVERALL_DISPLAY_HIGH_MIN}%—please verify highlighted fields before sharing.`,
    low: 'Low confidence detected. Manual validation recommended.',
  },

  // Inline Field Messages
  inlineField: {
    inferred: 'This value was inferred using AI.',
    multipleValues: 'Multiple values detected. Please confirm the correct one.',
    blankField: 'Field left blank due to unclear data.',
  },
} as const;

/**
 * Error & Exception Messages (Critical for Trust)
 */
export const ErrorMessages = {
  // System / Platform Errors
  system: {
    somethingWentWrong: 'Something went wrong on our side.',
    processingTrouble:
      "We're having trouble processing this document right now - please use another document to start your summarization",
    serviceDisruption: 'Temporary service disruption. Please try again shortly.',
    uploadFailed: 'Upload failed. Please try again.',
    failedToProcessFiles: 'Failed to process files. Please try again.',
    tooManyRequests: 'Too many requests. Please wait a moment and try again.',
    /** 429 from plan / storage quota — distinct from transient rate limits (retrying will not help until limits reset or plan changes). */
    quotaExceededGuidance:
      "You've hit a plan or storage limit for this billing period. Open Plan usage to see which meter is full. You can upgrade, wait until your monthly limits reset (UTC billing month), or reduce usage. If you use Docufy through an organization, your owner or admin manages seats, plan, and org billing—ask them if you need more capacity.",
    /** When plan-usage / quota APIs fail to load — avoid silent “empty” trust; limits still apply server-side. */
    planUsageMetersLoadFailed:
      "We couldn't load usage meters right now. Your plan limits still apply—try again in a moment. Open Plan & billing to review your subscription, or ask your organization admin if you're on a team plan.",
    /** Stripe portal / checkout URL could not be opened — user should still have a path. */
    billingPortalOpenFailed:
      'We could not open the billing page. Check your connection, allow pop-ups for this site, then try again. You can also open Plan & billing from your profile. Payment changes are completed in Stripe—update the card or plan there if a charge failed.',
    /** Appended when the API returned a specific Stripe/service message first. */
    billingPortalRecoveryHint:
      'Try again, allow pop-ups, or open Plan & billing. Card and plan changes are completed in Stripe’s secure page.',
    uploadTimeout:
      'The upload timed out. Try again, or use a smaller file if the problem continues.',
    networkError: 'Could not reach the server. Check your connection and try again.',
  },

  // AI-Specific Exceptions
  ai: {
    couldNotExtract: "We couldn't confidently extract data from this section.",
    layoutNotRecognized: 'Document layout is not recognized by our current models.',
    handwritingNotInterpreted: 'Handwriting could not be interpreted reliably.',
  },

  // Retry & Recovery
  retry: {
    retryProcessing: 'Retry processing',
    reuploadDocument: 'Re-upload document',
    skipAndContinue: 'Skip this step and continue',
  },
} as const;

/**
 * Permissions & Access Messages
 */
export const PermissionMessages = {
  noPermission: "You don't have permission to view this document.",
  deletedOrArchived: 'This document has been deleted or archived.',
  sessionExpired: 'Your session has expired. Please sign in again.',
  authenticationRequired: 'Authentication required. Please log in to upload files.',
} as const;

/**
 * Export, Download & Integration Messages
 */
export const ExportMessages = {
  // Export - In Progress
  inProgress: {
    preparing: 'Preparing your export…',
    generating: (format: string) => `Generating structured output (${format}).`,
  },

  // Export - Success
  success: {
    completed: 'Export completed successfully.',
    sentToSystem: 'Data sent to your connected system.',
  },

  // Export - Errors
  errors: {
    failed: 'Export failed. Please try again.',
    invalidCredentials: 'Integration credentials are invalid or expired.',
    connectionTimeout: 'Connection to external system timed out.',
  },
} as const;

/**
 * Notifications & Background Processing
 */
export const NotificationMessages = {
  willNotify: "You'll be notified when processing is complete.",
  backgroundProcessing: 'This document is processing in the background.',
  safeToLeave: 'You can safely leave this page.',
} as const;

/**
 * User Education & Confidence Builders (Subtle UX Copy)
 */
export const EducationMessages = {
  continuousImprovement: 'Docufy AI continuously improves with more documents.',
  dataSecurity: 'Your data is processed securely and never used to train public models.',
  compliance: 'All processing complies with PIPEDA & GDPR.',
} as const;

/**
 * Empty States & First-Time Use
 */
export const EmptyStateMessages = {
  noDocuments: 'No documents yet. Upload your first file to get started.',
  dragAndDrop: 'Drag and drop a document here, or browse your files.',
  needHelp: 'Need help? View a sample document.',
} as const;

/**
 * Action-Oriented CTA Messages
 */
export const ActionMessages = {
  reviewExtractedData: 'Review extracted data',
  approveAndContinue: 'Approve & continue',
  flagForCorrection: 'Flag for correction',
  sendForHumanReview: 'Send for human review',
} as const;

/**
 * Type definitions for message categories
 */
export type UploadMessageKey = keyof typeof UploadMessages;
export type PreProcessingMessageKey = keyof typeof PreProcessingMessages;
export type ProcessingMessageKey = keyof typeof ProcessingMessages;
export type SuccessMessageKey = keyof typeof SuccessMessages;
export type ReviewMessageKey = keyof typeof ReviewMessages;
export type ErrorMessageKey = keyof typeof ErrorMessages;
export type PermissionMessageKey = keyof typeof PermissionMessages;
export type ExportMessageKey = keyof typeof ExportMessages;
export type NotificationMessageKey = keyof typeof NotificationMessages;
export type EducationMessageKey = keyof typeof EducationMessages;
export type EmptyStateMessageKey = keyof typeof EmptyStateMessages;
export type ActionMessageKey = keyof typeof ActionMessages;

/**
 * Helper function to get upload progress message
 */
export function getUploadProgressMessage(
  fileCount: number,
  progress: number,
  isLargeFile: boolean = false,
): string {
  if (isLargeFile) {
    return UploadMessages.inProgress.largeFile;
  }
  if (fileCount > 1) {
    return UploadMessages.inProgress.uploadingMultiple(fileCount, progress);
  }
  return UploadMessages.inProgress.uploading;
}

const BACKEND_PROCESSING_ROTATE_SEC = 5;

/** Primary line while server-side processing runs (rotates so progress never looks stuck). */
export function getBackendProcessingRotatingMessage(elapsedSec: number): string {
  const { rotatingMessages } = UploadMessages.backendProcessing;
  const idx = Math.floor(elapsedSec / BACKEND_PROCESSING_ROTATE_SEC) % rotatingMessages.length;
  return rotatingMessages[idx];
}

/** Optional reassurance line with elapsed time (null when not shown yet). */
export function getBackendProcessingSecondaryLine(elapsedSec: number): string | null {
  if (elapsedSec < UploadMessages.backendProcessing.stillWorkingAfterSeconds) return null;
  if (elapsedSec < 60) return `Still working — ${elapsedSec}s`;
  const m = Math.floor(elapsedSec / 60);
  const s = elapsedSec % 60;
  return `Still working — ${m}m ${s}s`;
}

/**
 * Helper function to get processing page message
 */
export function getProcessingPageMessage(current: number, total: number): string {
  return ProcessingMessages.inProgress.processingPage(current, total);
}

/**
 * Helper function to get confidence message
 */
export function getConfidenceMessage(percentage: number): string {
  if (percentage >= CONFIDENCE_OVERALL_DISPLAY_HIGH_MIN) {
    return ReviewMessages.confidence.high(percentage);
  }
  if (percentage >= CONFIDENCE_LOW_FIELD_THRESHOLD) {
    return ReviewMessages.confidence.medium;
  }
  return ReviewMessages.confidence.low;
}

/**
 * Helper function to get export format message
 */
export function getExportFormatMessage(format: 'JSON' | 'CSV' | 'Excel'): string {
  return ExportMessages.inProgress.generating(format);
}
